import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { KnowledgeBaseService } from '../../../../core/services/knowledge-base.service';
import { KnowledgeArticleSummary, KnowledgeCategory } from '../../../../core/models';
import { ScrollReveal } from '../../../../shared/directives/scroll-reveal';

const SUPPORT_EMAIL = 'contact@youthconnektcameroon.cm';
const PAGE_SIZE = 6;

/**
 * UC-24 : centre d'aide public — même source réelle que documentation.ts (support-service,
 * /support/public/knowledge-base et /public/categories), en version compacte (recherche + articles
 * à la une + renvoi vers la fiche complète /documentation/:slug).
 *
 * Le contenu précédent inventait des filtres "profil" (Jeune/Startup/Partenaire/Évaluateur) et des
 * compteurs de consultations : aucun des deux n'existe côté backend (KnowledgeArticleSummary n'a
 * ni persona ni viewCount, voir support.model.ts). Remplacés respectivement par les vraies
 * catégories et par le champ réel `featured` pour mettre en avant certains articles en tête de
 * liste, plutôt que d'afficher des chiffres fabriqués.
 */
@Component({
  selector: 'app-contact',
  imports: [ReactiveFormsModule, RouterLink, ScrollReveal, TranslatePipe],
  templateUrl: './contact.html',
  styleUrl: './contact.css',
})
export class Contact {
  private readonly knowledgeBase = inject(KnowledgeBaseService);

  readonly supportEmail = SUPPORT_EMAIL;
  readonly queryControl = new FormControl('', { nonNullable: true });
  readonly categoryFilter = signal('');

  readonly categories = signal<KnowledgeCategory[]>([]);
  readonly articles = signal<KnowledgeArticleSummary[]>([]);
  readonly loading = signal(true);
  readonly error = signal(false);

  /** Les articles mis en avant par l'équipe (featured) remontent en premier, à défaut d'un compteur de consultations réel. */
  readonly sortedArticles = computed(() =>
    [...this.articles()].sort((a, b) => Number(b.featured) - Number(a.featured)),
  );

  constructor() {
    this.knowledgeBase.publicCategories().subscribe({
      next: (categories) => this.categories.set(categories),
      error: () => undefined,
    });
    this.search();
  }

  categoryName(code: string | null): string {
    if (!code) return '';
    return this.categories().find((c) => c.code === code)?.name ?? code;
  }

  onSubmit(event: Event): void {
    event.preventDefault();
    this.search();
  }

  setCategory(code: string): void {
    this.categoryFilter.set(code === this.categoryFilter() ? '' : code);
    this.search();
  }

  private search(): void {
    this.loading.set(true);
    this.error.set(false);
    this.knowledgeBase
      .publicArticles({
        query: this.queryControl.value.trim() || undefined,
        categoryCode: this.categoryFilter() || undefined,
        size: PAGE_SIZE,
      })
      .subscribe({
        next: (page) => {
          this.articles.set(page.content);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        },
      });
  }
}
